// ==================================================================================
// Digital Approval System (DAS) - Approvers Hook
// ==================================================================================
// Custom hooks for approver lookup operations
// ==================================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { approversService } from '../../../api/services/das/approversService';
import type {
  ApproverDto,
  ApproverQueryParameters,
  ApproverType,
  RoleDto,
  DepartmentDto,
} from '../../../api/types/das/approvers.types';

/**
 * Hook for fetching all approvers (users and roles)
 * @param params - Query parameters for filtering approvers
 * @param enabled - If false, skips automatic fetching on mount. Use refetch() to fetch manually. Default: true
 */
export function useApprovers(params?: ApproverQueryParameters, enabled: boolean = true) {
  const [approvers, setApprovers] = useState<ApproverDto[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  // Store params in a ref to avoid dependency issues
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchApprovers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await approversService.getAll(paramsRef.current);
      setApprovers(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch approvers');
      console.error('Failed to fetch approvers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch only once on mount if enabled
  useEffect(() => {
    if (enabled) {
      fetchApprovers();
    }
  }, [fetchApprovers, enabled]);

  return {
    approvers,
    loading,
    error,
    refetch: fetchApprovers,
  };
}

/**
 * Hook for fetching user approvers only
 */
export function useUserApprovers(params?: ApproverQueryParameters) {
  const [users, setUsers] = useState<ApproverDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await approversService.getUsers(paramsRef.current);
      setUsers(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch user approvers');
      console.error('Failed to fetch user approvers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    refetch: fetchUsers,
  };
}

/**
 * Hook for fetching role approvers only
 */
export function useRoleApprovers(params?: ApproverQueryParameters) {
  const [roles, setRoles] = useState<ApproverDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await approversService.getRoles(paramsRef.current);
      setRoles(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch role approvers');
      console.error('Failed to fetch role approvers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return {
    roles,
    loading,
    error,
    refetch: fetchRoles,
  };
}

/**
 * Hook for searching approvers
 */
export function useApproverSearch() {
  const [results, setResults] = useState<ApproverDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(
    async (query: string, type?: ApproverType, limit?: number) => {
      if (!query || query.trim().length === 0) {
        setResults([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await approversService.search(query, type, limit);
        setResults(data);
      } catch (err) {
        const error = err as Error;
        setError(error.message || 'Failed to search approvers');
        console.error('Failed to search approvers:', err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  return {
    results,
    loading,
    error,
    search,
    clearResults,
  };
}

/**
 * Hook for fetching all roles list
 */
export function useRoles(activeOnly: boolean = true) {
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeOnlyRef = useRef(activeOnly);
  activeOnlyRef.current = activeOnly;

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await approversService.getAllRoles(activeOnlyRef.current);
      setRoles(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch roles');
      console.error('Failed to fetch roles:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return {
    roles,
    loading,
    error,
    refetch: fetchRoles,
  };
}

/**
 * Hook for fetching all departments list
 */
export function useDepartments(activeOnly: boolean = true) {
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeOnlyRef = useRef(activeOnly);
  activeOnlyRef.current = activeOnly;

  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await approversService.getAllDepartments(activeOnlyRef.current);
      setDepartments(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch departments');
      console.error('Failed to fetch departments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  return {
    departments,
    loading,
    error,
    refetch: fetchDepartments,
  };
}

/**
 * Hook for fetching users by role ID
 */
export function useUsersByRole(roleId: string | null) {
  const [users, setUsers] = useState<ApproverDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!roleId) {
      setUsers([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await approversService.getUsersByRole(roleId);
      setUsers(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch users by role');
      console.error('Failed to fetch users by role:', err);
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    refetch: fetchUsers,
  };
}

/**
 * Hook for fetching users by department ID
 */
export function useUsersByDepartment(departmentId: string | null) {
  const [users, setUsers] = useState<ApproverDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!departmentId) {
      setUsers([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await approversService.getUsersByDepartment(departmentId);
      setUsers(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch users by department');
      console.error('Failed to fetch users by department:', err);
    } finally {
      setLoading(false);
    }
  }, [departmentId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    refetch: fetchUsers,
  };
}
